import type { SubtaskPhase } from '../types';

export const pickString = (value: unknown): string => (typeof value === 'string' ? value : '');

export interface SessionEventBase {
  type?: string;
  request_id?: string;
  conversation_id?: string;
  turn_id?: string;
  event_id?: string;
  stream_seq?: number;
  timestamp?: number;
  payload?: Record<string, unknown>;
}

export const tryParseSessionEvent = (payload: string): SessionEventBase | null => {
  try {
    const parsed = JSON.parse(payload);
    return parsed && typeof parsed === 'object' ? (parsed as SessionEventBase) : null;
  } catch {
    return null;
  }
};

export const mapToolStatus = (status: unknown): 'ok' | 'error' | undefined => {
  if (status === 'ok') return 'ok';
  if (typeof status === 'string' && status) return 'error';
  return undefined;
};

interface CoordinatorRaw {
  coordinator_id?: unknown;
  status?: unknown;
  phases?: Array<Record<string, unknown>>;
}

interface PhaseRaw {
  phase_id?: unknown;
  phase_name?: unknown;
  phase_status?: unknown;
  phase_description?: unknown;
  phase_strategy?: unknown;
  role_count?: unknown;
  roles_completed?: unknown;
  roles_running?: unknown;
  roles?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

function mapPhaseRaw(coordinatorId: string, p: PhaseRaw): SubtaskPhase {
  return {
    coordinatorId,
    phaseId: pickString(p.phase_id),
    phaseName: pickString(p.phase_name),
    phaseStatus: pickString(p.phase_status),
    phaseDescription: pickString(p.phase_description),
    phaseStrategy: pickString(p.phase_strategy),
    roleCount: typeof p.role_count === 'number' ? p.role_count : undefined,
    rolesCompleted: typeof p.roles_completed === 'number' ? p.roles_completed : undefined,
    rolesRunning: typeof p.roles_running === 'number' ? p.roles_running : undefined,
    roles: (p.roles ?? []).map((r) => ({
      roleName: pickString(r.role_name),
      roleStatus: pickString(r.role_status || r.status),
      roleNumber: typeof r.role_number === 'number' ? r.role_number : undefined,
      roleCount: typeof r.role_count === 'number' ? r.role_count : undefined,
      roleProgressPercent: typeof r.role_progress_percent === 'number' ? r.role_progress_percent : undefined,
      rolesCompleted: typeof r.roles_completed === 'number' ? r.roles_completed : undefined,
      rolesRunning: typeof r.roles_running === 'number' ? r.roles_running : undefined,
      rolesPending: typeof r.roles_pending === 'number' ? r.roles_pending : undefined,
    })),
  };
}

export function flattenCoordinatorPhases(coordinators: CoordinatorRaw[]): SubtaskPhase[] {
  return (coordinators ?? [])
    .filter((c) => c.status !== 'canceled' && c.status !== 'failed')
    .flatMap((c) => {
      const coordinatorId = pickString(c.coordinator_id);
      return (c.phases ?? []).map((p) => mapPhaseRaw(coordinatorId, p as PhaseRaw));
    });
}

export function handleCoordinatorCreated(
  payload: Record<string, unknown>,
  prev: SubtaskPhase[],
): SubtaskPhase[] | null {
  const coordinatorId = pickString(payload.coordinator_id);
  const coordinatorStatus = pickString(payload.coordinator_status || payload.status);
  if (!coordinatorId || coordinatorStatus === 'canceled') return null;

  const payloadPhases = (Array.isArray(payload.phases) ? payload.phases : []) as Record<string, unknown>[];
  const newPhases = payloadPhases.map((p) => mapPhaseRaw(coordinatorId, p as PhaseRaw));
  const filtered = prev.filter((p) => p.coordinatorId !== coordinatorId);
  return [...filtered, ...newPhases];
}

export function handleCoordinatorPhaseUpdated(
  payload: Record<string, unknown>,
  prev: SubtaskPhase[],
): SubtaskPhase[] | null {
  const coordinatorId = pickString(payload.coordinator_id);
  const phaseId = pickString(payload.phase_id);
  if (!coordinatorId || !phaseId) return null;

  const idx = prev.findIndex((p) => p.coordinatorId === coordinatorId && p.phaseId === phaseId);
  const updated: SubtaskPhase = {
    coordinatorId,
    phaseId,
    phaseName: pickString(payload.phase_name) || (idx >= 0 ? prev[idx].phaseName : ''),
    phaseStatus: pickString(payload.phase_status),
    phaseDescription: pickString(payload.phase_description) || (idx >= 0 ? prev[idx].phaseDescription : undefined),
    phaseStrategy: pickString(payload.phase_strategy) || (idx >= 0 ? prev[idx].phaseStrategy : undefined),
    roleCount:
      (typeof payload.role_count === 'number' ? payload.role_count : undefined) ??
      (idx >= 0 ? prev[idx].roleCount : undefined),
    rolesCompleted:
      (typeof payload.roles_completed === 'number' ? payload.roles_completed : undefined) ??
      (idx >= 0 ? prev[idx].rolesCompleted : undefined),
    rolesRunning:
      (typeof payload.roles_running === 'number' ? payload.roles_running : undefined) ??
      (idx >= 0 ? prev[idx].rolesRunning : undefined),
    roles: idx >= 0 ? prev[idx].roles : undefined,
  };

  if (idx >= 0) {
    const next = [...prev];
    next[idx] = updated;
    return next;
  }
  return [...prev, updated];
}
